import { useEffect, useState } from 'react';
import { api, resolveImageUrl } from '../lib/api.js';
import { KEEPA_DOMAINS } from './keepa-domains.js';

interface TrackedAsin {
  id: number;
  asin: string;
  domainId: number;
  label: string | null;
  lastChecked: string | null;
  createdAt: string;
  title: string | null;
  brand: string | null;
  imageUrl: string | null;
  salesRank: number | null;
  priceBuyBox: number | null;
  rating: number | null;
  reviewCount: number | null;
  monthlySold: number | null;
  lastSynced: string | null;
}

interface Account { id: string; name: string; }

const DOMAIN_LABELS: Record<number, string> = { 1: 'US', 2: 'UK', 3: 'DE', 4: 'FR', 6: 'CA', 8: 'IT', 9: 'ES', 11: 'MX' };
const DOMAIN_URL: Record<number, string> = { 1: 'amazon.com', 2: 'amazon.co.uk', 3: 'amazon.de', 4: 'amazon.fr', 6: 'amazon.ca', 8: 'amazon.it', 9: 'amazon.es', 11: 'amazon.com.mx' };

const LABEL_STYLES: Record<string, string> = {
  own: 'bg-green-100 text-green-700 border-green-200',
  competitor: 'bg-red-100 text-red-700 border-red-200',
  research: 'bg-blue-100 text-blue-700 border-blue-200',
};

export function KeepaTracking() {
  const [allTracked, setAllTracked] = useState<TrackedAsin[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<number | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [syncing, setSyncing] = useState(false);

  // Add form
  const [addAsins, setAddAsins] = useState('');
  const [addLabel, setAddLabel] = useState<'own' | 'competitor' | 'research'>('research');
  const [addMarketplace, setAddMarketplace] = useState('US');
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [zoomImage, setZoomImage] = useState<string | null>(null);

  useEffect(() => {
    api.get<Account[]>('/accounts').then(async accts => {
      setAccounts(accts);
      if (accts.length === 0) { setLoading(false); return; }
      // Merge all accounts
      const results = await Promise.all(
        accts.map(a => api.get<{ tracked: TrackedAsin[] }>(`/keepa/tracked/${a.id}`).catch(() => ({ tracked: [] }))),
      );
      const merged: TrackedAsin[] = [];
      for (const r of results) merged.push(...r.tracked);
      setAllTracked(merged);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const reload = async () => {
    const results = await Promise.all(
      accounts.map(a => api.get<{ tracked: TrackedAsin[] }>(`/keepa/tracked/${a.id}`).catch(() => ({ tracked: [] }))),
    );
    const merged: TrackedAsin[] = [];
    for (const r of results) merged.push(...r.tracked);
    setAllTracked(merged);
  };

  const handleAdd = async () => {
    if (accounts.length === 0 || !addAsins.trim()) return;
    setAdding(true); setError(''); setSuccess('');
    const asins = addAsins.split(/[,\s\n]+/).map(a => a.trim().toUpperCase()).filter(a => a.length === 10);
    if (asins.length === 0) { setError('No valid ASINs (must be 10 chars)'); setAdding(false); return; }
    try {
      const resp = await api.post<{ ok: boolean; added: number }>(`/keepa/tracked/${accounts[0].id}`, {
        asins, domainId: KEEPA_DOMAINS[addMarketplace] ?? 1, label: addLabel,
      });
      setSuccess(`Added ${resp.added} ASIN(s)`);
      setAddAsins('');
      await reload();
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed'); }
    finally { setAdding(false); }
  };

  const handleRemove = async (asin: string, domainId: number) => {
    if (accounts.length === 0) return;
    try {
      await api.delete(`/keepa/tracked/${accounts[0].id}/${asin}?domainId=${domainId}`);
      setAllTracked(prev => prev.filter(t => !(t.asin === asin && t.domainId === domainId)));
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed'); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await fetch('https://sellvana-sync.vandreus.workers.dev/trigger/keepa', { method: 'POST' });
      setSuccess('Sync triggered — data will refresh shortly');
      setTimeout(reload, 10000);
    } catch { setError('Sync trigger failed'); }
    finally { setSyncing(false); }
  };

  // Filters
  const domains = [...new Set(allTracked.map(t => t.domainId))].sort();
  const labels = [...new Set(allTracked.map(t => t.label).filter(Boolean))] as string[];
  const filtered = allTracked.filter(t =>
    (!selectedDomain || t.domainId === selectedDomain) &&
    (!selectedLabel || t.label === selectedLabel),
  );

  const ownCount = allTracked.filter(t => t.label === 'own').length;
  const compCount = allTracked.filter(t => t.label === 'competitor').length;
  void allTracked.filter(t => t.label === 'research').length; // research count available in label filter buttons

  if (loading) return <div className="space-y-4 animate-pulse">{[1,2,3].map(i => <div key={i} className="h-20 rounded-xl bg-gray-200" />)}</div>;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Keepa Tracking</h2>
          <p className="text-sm text-gray-500">Manage tracked ASINs — daily BSR, pricing, and reviews from Keepa</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleSync} disabled={syncing}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50">
            {syncing ? 'Syncing...' : 'Sync Now'}
          </button>
          <button onClick={() => setShowForm(!showForm)}
            className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700">
            {showForm ? 'Cancel' : '+ Add ASINs'}
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid gap-3 sm:grid-cols-4">
        <SummaryCard label="Total Tracked" value={allTracked.length} />
        <SummaryCard label="Own Products" value={ownCount} color="green" />
        <SummaryCard label="Competitors" value={compCount} color="red" />
        <SummaryCard label="Daily Tokens" value={`~${allTracked.length * 4}`} sub="/day" />
      </div>

      {/* Add form */}
      {showForm && (
        <div className="rounded-xl border border-brand-200 bg-brand-50/30 p-4 space-y-3">
          <textarea value={addAsins} onChange={e => setAddAsins(e.target.value)}
            placeholder="Enter ASINs (comma, space, or newline separated)"
            rows={2} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          <div className="flex items-center gap-3">
            <select value={addMarketplace} onChange={e => setAddMarketplace(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
              {Object.keys(KEEPA_DOMAINS).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="flex gap-1">
              {(['own', 'competitor', 'research'] as const).map(l => (
                <button key={l} onClick={() => setAddLabel(l)}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                    addLabel === l ? LABEL_STYLES[l] : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}>{l}</button>
              ))}
            </div>
            <button onClick={handleAdd} disabled={adding || !addAsins.trim()}
              className="rounded-lg bg-brand-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
              {adding ? 'Adding...' : 'Add'}
            </button>
          </div>
          {success && <p className="text-sm text-green-600">{success}</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}

      {/* Filters: market + label */}
      <div className="flex flex-wrap gap-2">
        {domains.length > 1 && (
          <>
            <button onClick={() => setSelectedDomain(null)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                !selectedDomain ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}>All</button>
            {domains.map(d => (
              <button key={d} onClick={() => setSelectedDomain(d === selectedDomain ? null : d)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                  selectedDomain === d ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}>{DOMAIN_LABELS[d] ?? d}</button>
            ))}
            <div className="w-px bg-gray-200 mx-1" />
          </>
        )}
        {labels.map(l => (
          <button key={l} onClick={() => setSelectedLabel(l === selectedLabel ? null : l)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              selectedLabel === l ? LABEL_STYLES[l] : 'border-gray-200 text-gray-500 hover:bg-gray-50'
            }`}>{l} ({allTracked.filter(t => t.label === l).length})</button>
        ))}
      </div>

      {/* Product cards */}
      <div className="space-y-2">
        {filtered.map(t => (
          <div key={`${t.asin}-${t.domainId}`}
            className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 hover:bg-gray-50 transition-colors">
            {/* Thumbnail */}
            <div className="h-12 w-12 flex-shrink-0 rounded-lg border border-gray-100 bg-gray-50 overflow-hidden cursor-pointer"
              onClick={() => t.imageUrl && setZoomImage(resolveImageUrl(t.imageUrl))}>
              {t.imageUrl ? (
                <img src={resolveImageUrl(t.imageUrl) ?? ''} alt="" className="h-full w-full object-contain" loading="lazy" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-gray-300 text-xs">?</div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <a href={`https://www.${DOMAIN_URL[t.domainId] ?? 'amazon.com'}/dp/${t.asin}`}
                  target="_blank" rel="noopener noreferrer"
                  className="font-mono text-xs text-brand-600 hover:underline">{t.asin}</a>
                <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${LABEL_STYLES[t.label ?? ''] ?? 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                  {t.label}
                </span>
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                  {DOMAIN_LABELS[t.domainId] ?? t.domainId}
                </span>
              </div>
              <p className="text-xs text-gray-500 truncate max-w-[400px]">{t.title ?? '—'}</p>
            </div>

            {/* Metrics */}
            <div className="flex items-center gap-4 flex-shrink-0 text-xs">
              <Metric label="BSR" value={t.salesRank ? `#${t.salesRank.toLocaleString()}` : '—'} />
              <Metric label="Price" value={t.priceBuyBox != null && t.priceBuyBox > 0 ? `$${t.priceBuyBox.toFixed(2)}` : '—'} />
              <Metric label="Rating" value={t.rating != null && t.rating > 0 ? t.rating.toFixed(1) : '—'} />
              <Metric label="Reviews" value={t.reviewCount ? t.reviewCount.toLocaleString() : '—'} />
              <Metric label="Sold/mo" value={t.monthlySold ? t.monthlySold.toLocaleString() : '—'} />
            </div>

            {/* Remove */}
            <button onClick={() => handleRemove(t.asin, t.domainId)}
              className="flex-shrink-0 rounded p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
              title="Remove from tracking">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-400">
            {allTracked.length === 0 ? 'No ASINs tracked yet. Click "+ Add ASINs" to start.' : 'No products match the selected filters.'}
          </div>
        )}
      </div>

      {/* Image zoom modal */}
      {zoomImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setZoomImage(null)}>
          <div className="relative max-w-md max-h-[80vh] bg-white rounded-2xl p-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <button onClick={() => setZoomImage(null)}
              className="absolute -top-2 -right-2 rounded-full bg-white shadow-md p-1 hover:bg-gray-100">
              <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img src={zoomImage} alt="" className="max-h-[70vh] w-auto object-contain rounded-lg" />
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color, sub }: { label: string; value: number | string; color?: string; sub?: string }) {
  const textColor = color === 'green' ? 'text-green-600' : color === 'red' ? 'text-red-600' : 'text-gray-900';
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{label}</p>
      <p className={`text-xl font-bold ${textColor}`}>{value}{sub && <span className="text-xs font-normal text-gray-400">{sub}</span>}</p>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center min-w-[50px]">
      <p className="text-[10px] text-gray-400">{label}</p>
      <p className="font-medium text-gray-700">{value}</p>
    </div>
  );
}
